import { extend } from "vee-validate";
import { Guid } from "guid-typescript";
import { required, email, min, min_value, max, numeric, size, oneOf, between, alpha_spaces, ext, excluded, confirmed, double, regex, is_not } from "vee-validate/dist/rules";
// BUILT-IN VALIDATION RULES
// https://logaretm.github.io/vee-validate/guide/rules.html#rules
extend("required", {
    ...required,
    message: "{_field_} is required",
});
extend("email", {
    ...email,
    message: "Email address is invalid",
});
extend("double", {
    ...double,
    message: "{_field_} is invalid",
});
extend("regex", {
    ...regex,
    message: "{_field_} is invalid",
});
extend("min", {
    ...min,
    message: "Field is too short",
});
extend("min_value", {
    ...min_value,
    message: "{_field_} must be greater than 0.",
});
extend("max", {
    ...max,
    message: "Field is too long",
});
extend("numeric", {
    ...numeric,
    message: "Field allows numbers only",
});
extend("size", {
    ...size,
    message: "File should not exceed {size} KiloBytes.",
});
extend("isNot", {
    ...is_not,
    message: "{_field_} selection is required",
});
extend("oneOf", {
    ...oneOf,
    message: "Selection is mandatory",
});
extend("excluded", {
    ...excluded,
    message: "Selection is mandatory",
});
extend("between", {
    ...between,
    message: "The {_field_} value must be between {min} and {max}",
});
extend("alpha_spaces", {
    ...alpha_spaces,
    message: "Only characters and spaces allowed",
});
extend("answer", {
    ...required,
    message: "An answer is required",
});
extend("ext", {
    ...ext,
    message: "Only PDF, doc and docx format are allowed.",
});
extend("confirmed", {
    ...confirmed,
    message: "Both Passwords do not match.",
});
extend("daterangecheck", {
    validate: (value) => {
        var startDate = value[0];
        var endDate = value[1];
        return startDate instanceof Date && endDate instanceof Date;
    },
    message: "{_field_} should contain at least 2 valid datetimes.",
});
extend("datecheck", {
    validate: (inputDate) => {
        return inputDate instanceof Date;
    },
    message: "{_field_} should contain a date.",
});
// CUSTOM VALIDATION RULES
extend("numeric_or_null", {
    ...numeric,
    message: "Field allows numbers only",
    validate: (value) => value === null || numeric.validate(value),
});
// Optional leading +, then digits, spaces, - and ( ) (formatted / international numbers); length is checked by phone_digits and max
extend("phone", {
    validate: (value) => /^\+?[0-9\s\-()]+$/.test((value || "").trim()),
    message: "{_field_} may contain only digits, spaces, +, - and brackets.",
});
extend("phone_digits", {
    validate: (value) => (value || "").replace(/\D/g, "").length >= 7,
    message: "{_field_} must have at least 7 digits.",
});
// Rejects names made only of symbols, e.g. "@#$%^&*"
extend("has_alphanumeric", {
    validate: (value) => /[\p{L}\p{N}]/u.test(value || ""),
    message: "{_field_} must contain at least one letter or number.",
});
// For checks the form computes itself (cross-field or duplicate checks): `:rules="{ invalid_when: condition }"`
// fails while the condition is true and is dropped while it's false. The form gives the message through the
// provider's `:custom-messages="{ invalid_when: '...' }"`.
extend("invalid_when", {
    validate: () => false,
    message: "{_field_} is invalid",
});
// Rejects HTML tags, e.g. "<img src=x>"; a "<" not followed by a letter, "/" or "!" (as in "a < b") is allowed.
// Matches ClientRiskValidator.NotContainHtmlTag on the API.
extend("no_html", {
    validate: (value) => !/<\s*[a-zA-Z/!][^>]*>/.test(value || ""),
    message: "{_field_} cannot contain HTML tags.",
});
// Postal code for the selected Country (passed by name): India PIN 6 digits, US ZIP / ZIP+4, otherwise a generic 3-10 character code
export const isIndia = (country) => (country || "").trim().toLowerCase() === "india";
const isUnitedStates = (country) => ["usa", "united states", "united states of america", "us"].includes((country || "").trim().toLowerCase());
extend("postal_code", {
    params: ["country"],
    validate: (value, { country }) => {
        const code = (value || "").trim();
        if (isIndia(country)) {
            return /^\d{6}$/.test(code);
        }
        if (isUnitedStates(country)) {
            return /^\d{5}(-\d{4})?$/.test(code);
        }
        return /^[A-Za-z0-9][A-Za-z0-9\s-]{1,8}[A-Za-z0-9]$/.test(code);
    },
    message: (field, { country }) => {
        if (isIndia(country)) {
            return "PIN Code must be 6 digits, e.g. 682001.";
        }
        if (isUnitedStates(country)) {
            return "ZIP Code must be 5 digits or ZIP+4, e.g. 12345 or 12345-6789.";
        }
        return "ZIP Code must be 3-10 letters or digits (spaces and - allowed).";
    },
});
// Interaction mode for passive forms: silent until the first submit, but a field that
// shows an error re-validates as it's typed in, so the message clears once it's fixed
export const RECHECK_MODE = ({ errors }) => ({ on: errors.length ? ["input", "change"] : [] });
// http(s) link with a host that has a domain suffix, e.g. https://www.linkedin.com/in/name
extend("url", {
    validate: (value) => /^https?:\/\/([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/\S*)?$/i.test((value || "").trim()),
    message: "{_field_} must be a valid link starting with http:// or https://",
});
extend("guidNotEmpty", {
    validate: (value) => {
        if (typeof value == "object") {
            return !value.isEmpty();
        }
        var selectedVal = Guid.parse(value);
        return !selectedVal.isEmpty();
    },
    message: "{_field_} is required",
});
extend("endTimeAfterStartTime", {
    validate(value, args) {
        const startTime = args.startTime;
        const startTotalHours = convertTimeToHours(startTime);
        const endTotalHours = convertTimeToHours(value);
        return endTotalHours > startTotalHours;
    },
    params: ["startTime"],
    message: "End time must be later than start time",
});
extend("startTimeBeforeEndTime", {
    validate(value, args) {
        const endTime = args.endTime;
        const startTotalHours = convertTimeToHours(value);
        const endTotalHours = convertTimeToHours(endTime);
        return startTotalHours < endTotalHours;
    },
    params: ["endTime"],
    message: "Start time must be earlier than end time",
});
extend("participantsCheck", {
    validate(value, args) {
        if (!args.participants) {
            return false;
        }
        const participants = Array.isArray(args.participants) ? args.participants : [args.participants];
        const validParticipants = participants.filter((participant) => participant !== undefined);
        if (validParticipants.length === 0) {
            return false;
        }
        const hasSelectedEditableParticipant = validParticipants.some((participant) => !participant.isUnEditable && participant.isSelected);
        return hasSelectedEditableParticipant;
    },
    params: ["participants"],
    message: "At least one participant is required.",
});
function convertTimeToHours(timeString) {
    const [time, period] = timeString.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    if (period === "PM" && hours !== 12) {
        hours += 12;
    }
    else if (period === "AM" && hours === 12) {
        hours = 0;
    }
    return hours * 60 + minutes;
}
//extend('is_earlier', {
//    validate: (value, { compare }) => {
//        return date_compare({ value, compare, validationType: 'earlier' });
//    },
//    params: ['compare', 'dateType'],
//    message: 'The selected date must not be earlier than {dateType}'
//});
//extend('is_beyond', {
//    validate: (value, { compare }) => {
//        return date_compare({ value, compare, validationType: 'beyond' });
//    },
//    params: ['compare', 'dateType'],
//    message: 'The selected date must not be older than {dateType}'
//});
